import { Descriptions, Input, Select, Switch, Tag } from 'antd'
import {
  FieldCategory,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/formSection/formSectionTypes'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import { useTicketStepContext } from 'features/libra/ticket/TicketStateContext'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'

import { DynamicFieldTooltip } from './tooltips/DynamicFieldTooltip'

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LabelWithTooltipContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

interface NiiScreeningFormSectionProps {
  config: FormSection
  ontologyFields: Record<string, OntologyField>
  fieldValues: Record<string, unknown>
  setFieldValues: Dispatch<SetStateAction<Record<string, unknown>>>
}

/**
 * Get the input field from ontology, filtering out non-input fields
 */
function getInputField(
  ontologyFields: Record<string, OntologyField>,
  fieldId: string
): InputField | undefined {
  const field = ontologyFields[fieldId]
  if (field && field.category === FieldCategory.INPUT) {
    return field as InputField
  }
  return undefined
}

/**
 * Renders a form section as a card with field inputs in Descriptions format.
 * This preserves the NII Screening card-based UI while being config-driven.
 */
export const NiiScreeningFormSection: FC<NiiScreeningFormSectionProps> = ({
  config,
  ontologyFields,
  fieldValues,
  setFieldValues,
}) => {
  const { isStepComplete } = useTicketStepContext()
  const title = config.title ?? config.id

  // Handler for updating a field value
  const handleFieldChange = useCallback(
    (fieldId: string) => (value: unknown) => {
      setFieldValues((prev) => {
        const updated = { ...prev }

        if (value === undefined || value === null || value === '') {
          delete updated[fieldId]
        } else {
          updated[fieldId] = value
        }

        return updated
      })
    },
    [setFieldValues]
  )

  // Get filtered fields with their ontology definitions
  const fields = useMemo(() => {
    return config.fields
      .map((fieldId) => {
        const ontologyField = getInputField(ontologyFields, fieldId)
        return { fieldId, ontologyField }
      })
      .filter(
        (f): f is { fieldId: string; ontologyField: InputField } =>
          f.ontologyField !== undefined
      )
  }, [config.fields, ontologyFields])

  // Render a field label with optional tooltip
  const renderFieldLabel = (field: InputField) => {
    // Use tooltip object if available
    if (field.tooltip) {
      return (
        <LabelWithTooltipContainer>
          {field.name}
          <DynamicFieldTooltip tooltip={field.tooltip} />
        </LabelWithTooltipContainer>
      )
    }

    return field.name
  }

  return (
    <StyledCard title={title}>
      <Descriptions column={1} bordered>
        {fields.map(({ fieldId, ontologyField }) => (
          <Descriptions.Item
            key={fieldId}
            label={renderFieldLabel(ontologyField)}>
            <FieldRenderer
              field={ontologyField}
              value={fieldValues[fieldId]}
              onChange={handleFieldChange(fieldId)}
              disabled={isStepComplete}
            />
          </Descriptions.Item>
        ))}
      </Descriptions>
    </StyledCard>
  )
}

interface FieldRendererProps {
  field: InputField
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}

const FIELD_RENDERERS: Record<
  string,
  (props: {
    field: InputField
    value: unknown
    onChange: (value: unknown) => void
  }) => React.ReactNode
> = {
  boolean: ({ value, onChange }) => (
    <SwitchRow>
      <Switch checked={!!value} onChange={(checked) => onChange(checked)} />
      <span>{value ? 'Yes' : 'No'}</span>
    </SwitchRow>
  ),
  enum: ({ field, value, onChange }) => (
    <EnumSelect
      value={value as string | undefined}
      onChange={onChange}
      allowedValues={field.allowedValues ?? []}
    />
  ),
  text: ({ field, value, onChange }) => (
    <Input.TextArea
      value={value as string | undefined}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      placeholder={`Enter ${field.name.toLowerCase()}...`}
    />
  ),
  number: ({ field, value, onChange }) => (
    <Input
      type='number'
      value={value as number | undefined}
      onChange={(e) => onChange(Number(e.target.value))}
      placeholder={`Enter ${field.name.toLowerCase()}...`}
    />
  ),
  date: ({ value, onChange }) => (
    <Input
      type='date'
      value={value as string | undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  ),
}

/**
 * Renders an individual field based on its type from the ontology definition.
 */
const FieldRenderer: FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  disabled,
}) => {
  // Read-only display when disabled
  if (disabled) {
    return <ReadOnlyValue value={value} field={field} />
  }

  const renderer = FIELD_RENDERERS[field.type]
  if (renderer) {
    return <>{renderer({ field, value, onChange })}</>
  }

  // Default fallback
  return (
    <Input
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

interface EnumSelectProps {
  value: string | undefined
  onChange: (value: unknown) => void
  allowedValues: ReadonlyArray<string>
}

const ENUM_COLORS: Record<string, string> = {
  low: 'red',
  med: 'orange',
  high: 'green',
  true: 'green',
  false: 'red',
  unknown: 'orange',
}

const EnumSelect: FC<EnumSelectProps> = ({
  value,
  onChange,
  allowedValues,
}) => {
  const options = useMemo(
    () =>
      allowedValues.map((v) => ({
        value: v,
        label: v.charAt(0).toUpperCase() + v.slice(1),
      })),
    [allowedValues]
  )

  return (
    <Select
      value={value}
      onChange={onChange}
      style={{ minWidth: 120 }}
      allowClear
      placeholder='Select...'>
      {options.map((opt) => (
        <Select.Option key={opt.value} value={opt.value}>
          {ENUM_COLORS[opt.value] ? (
            <Tag color={ENUM_COLORS[opt.value]}>{opt.label}</Tag>
          ) : (
            opt.label
          )}
        </Select.Option>
      ))}
    </Select>
  )
}

interface ReadOnlyValueProps {
  value: unknown
  field: InputField
}

const ReadOnlyValue: FC<ReadOnlyValueProps> = ({ value, field }) => {
  if (value === undefined || value === null) {
    return <span style={{ color: '#999' }}>—</span>
  }

  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>
  }

  if (field.type === 'enum') {
    const strValue = String(value)
    const color = getEnumTagColor(strValue)
    const label = strValue.charAt(0).toUpperCase() + strValue.slice(1)
    return color ? <Tag color={color}>{label}</Tag> : <span>{label}</span>
  }

  return <span>{String(value)}</span>
}

const getEnumTagColor = (value: string): string | undefined =>
  ENUM_COLORS[value]

export default React.memo(NiiScreeningFormSection)
